import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Check, Phone, Calendar, Clock, MessageSquare, TrendingUp, Zap, ArrowRight, BarChart3, Shield, Sparkles, CheckCircle, Users, Bell, Star, Database, Lock, Sliders, MonitorPlay, Layers, Smartphone, Target, DollarSign, MousePointer } from 'lucide-react';

// Import Assets
import anyaImg from '../assets/anya.png';
import amyImg from '../assets/amy.png';
import eliseAvatar from '../assets/elise-avatar.png';
import anaImg from '../assets/ana.png';
import andrewImg from '../assets/andrew.png';

const MarketingCoordinatorPage = () => {
  const [activeTab, setActiveTab] = useState('speed-to-lead');

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.5; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        .animate-pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background - Matching Receptionist Page */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Anya, Your Marketing Lead Coordinator</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight tracking-tight">
                <span className="text-gray-900">Stop Losing Leads to</span><br />
                <span className="text-blue-600">Voicemail</span>
              </h1>
              
              <p className="text-base text-gray-700 leading-relaxed max-w-lg">
                When you run ads, speed is everything. Anya instantly calls every new lead within seconds, educates them about your practice, and books them directly into your schedule.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <button className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all bg-white text-gray-900 hover:bg-gray-50 border border-gray-300 shadow-sm">
                    Get a demo
                  </button>
                </Link>
                <button className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md">
                  See pricing
                </button>
              </div>
            </div>
            
            {/* Right: Interactive AI Visual */}
            <div className="relative flex items-center justify-center h-[500px]">
              {/* Main Profile with Card */}
              <div className="relative">
                {/* Main Image Card with Gradient Glow */}
                <div className="relative p-1 rounded-3xl bg-gradient-to-br from-blue-300 via-blue-400 to-indigo-400 shadow-xl">
                  <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-[22px] overflow-hidden w-72 h-96">
                    <img
                      src={anyaImg}
                      alt="Anya Marketing Coordinator"
                      className="w-full h-full object-cover opacity-95"
                    />
                  
                  {/* Glowing circle indicator on ear */}
                  <div className="absolute top-32 right-12">
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full bg-blue-300 animate-pulse"></div>
                      <div className="absolute inset-0 w-8 h-8 rounded-full bg-blue-200 animate-ping"></div>
                    </div>
                  </div>
                  
                  {/* Audio waveform at bottom */}
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-2xl px-6 py-3 shadow-md border border-gray-200">
                    <div className="flex items-center gap-1">
                      {[4, 8, 6, 10, 5, 9, 4, 7, 11, 6, 8, 5].map((height, i) => (
                        <div 
                          key={i} 
                          className="w-1 bg-blue-500 rounded-full"
                          style={{ height: `${height * 2}px` }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
                </div>
                
                {/* Speech bubble - top left */}
                <div className="absolute -top-4 -left-16 bg-white rounded-2xl p-4 shadow-md w-60 border border-gray-200">
                  <p className="text-xs text-gray-600 leading-relaxed">"Hi! I saw you just inquired about Invisalign on Facebook. Do you have a moment to chat?"</p>
                  {/* Tail */}
                  <div className="absolute bottom-6 right-0 w-3 h-3 bg-white transform rotate-45 translate-x-1.5 border-r border-b border-gray-200"></div>
                </div>
                
                {/* Response Assistant badge - bottom left */}
                <div className="absolute bottom-24 -left-12 bg-white rounded-xl px-3 py-2 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Zap className="w-3.5 h-3.5 text-yellow-500" />
                    <span className="text-xs font-medium text-gray-600">Instant Outreach</span>
                  </div>
                </div>
                
                {/* Stats card - top right */}
                <div className="absolute top-4 -right-16 bg-white rounded-xl p-3 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-xs font-medium text-gray-700">3x Conversion</span>
                  </div>
                </div>
                
                {/* Circular stats - bottom right */}
                <div className="absolute -bottom-8 -right-20 bg-white rounded-2xl p-4 shadow-md border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="relative w-16 h-16">
                      {/* Semi-circle graph */}
                      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831"
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="3"
                          strokeDasharray="100, 100"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-gray-700">100%</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-2 h-2 rounded-full bg-green-400"></div>
                        <span className="text-xs text-gray-500">Contacted</span>
                      </div>
                      <p className="text-xs font-semibold text-gray-700">Lead Response</p>
                      <p className="text-xs text-gray-400">&lt; 1 min Delay</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - Text Left, Audio Right */}
      <section className="py-20 px-6 bg-white overflow-hidden border-b border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-8 relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium border border-blue-100 mb-4 uppercase tracking-wide">
                  Speed to Lead
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  Reach leads while they're still <span className="text-blue-600">thinking about you</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  When a patient submits a form on Facebook or Google, you have less than 5 minutes before they move on to a competitor. Anya triggers a call instantly, catching them at the peak of their interest.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mt-4">
                  She doesn't just say hello—she's trained to sell your practice, answer questions about treatments, and close the deal by booking an appointment right then and there.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/book-demo">
                  <button className="px-8 py-4 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5">
                    Book a Demo
                  </button>
                </Link>
                <button className="px-8 py-4 rounded-xl font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all">
                  View Pricing
                </button>
              </div>

            </div>

            {/* Right: Audio Demo Player */}
            <div className="relative">
              {/* Background Decorative Elements */}
              <div className="absolute -top-10 -right-10 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
              <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
              
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                {/* Player Header */}
                <div className="bg-[#0f172a] p-8 text-white">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">Hear Anya in Action</h3>
                      <p className="text-gray-400 text-sm">Converting leads to patients instantly</p>
                    </div>
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <div className="flex items-end gap-1 h-5">
                        <div className="w-1 bg-blue-400 rounded-full h-3 animate-pulse"></div>
                        <div className="w-1 bg-blue-400 rounded-full h-5 animate-pulse delay-75"></div>
                        <div className="w-1 bg-blue-400 rounded-full h-2 animate-pulse delay-150"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Playlist */}
                <div className="p-6 space-y-4">
                  {/* Track 1 */}
                  <div className="group bg-gray-50 hover:bg-blue-50 rounded-xl p-4 transition-all border border-gray-100 hover:border-blue-100 cursor-pointer">
                    <div className="flex items-center gap-4">
                      <button className="w-12 h-12 rounded-full bg-white shadow-sm group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center transition-all text-blue-600">
                        <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5"></div>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">Invisalign Lead Follow-up</h4>
                          <span className="text-xs font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">2:15</span>
                        </div>
                        <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">Calling a Facebook lead within 30 seconds</p>
                      </div>
                    </div>
                  </div>

                  {/* Track 2 */}
                  <div className="group bg-gray-50 hover:bg-blue-50 rounded-xl p-4 transition-all border border-gray-100 hover:border-blue-100 cursor-pointer">
                    <div className="flex items-center gap-4">
                      <button className="w-12 h-12 rounded-full bg-white shadow-sm group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center transition-all text-blue-600">
                        <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5"></div>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">Implant Consultation</h4>
                          <span className="text-xs font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">2:45</span>
                        </div>
                        <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">Qualifying a high-value patient lead</p>
                      </div>
                    </div>
                  </div>

                  {/* Track 3 */}
                  <div className="group bg-gray-50 hover:bg-blue-50 rounded-xl p-4 transition-all border border-gray-100 hover:border-blue-100 cursor-pointer">
                    <div className="flex items-center gap-4">
                      <button className="w-12 h-12 rounded-full bg-white shadow-sm group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center transition-all text-blue-600">
                        <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5"></div>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">Google Ads Inquiry</h4>
                          <span className="text-xs font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">1:58</span>
                        </div>
                        <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">Handling price objections and booking</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Player Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-3.5 h-3.5" />
                    <span>Conversion Optimized</span>
                  </div>
                  <span>Persuasive & Empathetic</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: The Only AI That Maximizes Your Marketing ROI */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-16 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Get The Most Value From <br/>
                    <span className="text-blue-600">Your Marketing Spend</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                    Simply setting up Anya can drive thousands in additional production per month if you already have ads running. She ensures no lead is left behind.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Card 1: Instant Outreach Engine */}
                <div className="flex flex-col">
                    {/* Visual Area - Glassmorphic on Gradient */}
                    <div className="bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         {/* Background Orbs */}
                         <div className="absolute top-0 right-0 w-64 h-64 bg-blue-300/30 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
                         <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-300/30 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
                         
                         {/* Glass Card Container */}
                         <div className="relative w-full max-w-md">
                            {/* Main Active Call Card */}
                            <div className="glass-card rounded-3xl p-6 relative z-20 bg-white/60 backdrop-blur-xl border border-white/60 shadow-xl">
                                <div className="flex items-start justify-between mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="relative">
                                            <div className="w-14 h-14 rounded-full overflow-hidden ring-4 ring-white shadow-md bg-blue-100 flex items-center justify-center">
                                                <span className="text-2xl font-bold text-blue-600">f</span>
                                            </div>
                                            <div className="absolute -bottom-1 -right-1 bg-green-500 w-4 h-4 rounded-full border-2 border-white"></div>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-lg">New Facebook Lead</h3>
                                            <p className="text-sm text-blue-600 font-medium">Form Submitted: Just now</p>
                                        </div>
                                    </div>
                                    <div className="bg-blue-600/10 px-3 py-1 rounded-full">
                                        <span className="text-xs font-bold text-blue-700 flex items-center gap-1">
                                            <Phone className="w-3 h-3" />
                                            Calling
                                        </span>
                                    </div>
                                </div>

                                {/* Logic Visualization */}
                                <div className="space-y-3">
                                    {/* Trigger */}
                                    <div className="flex items-center gap-3 animate-float" style={{ animationDelay: '0s' }}>
                                        <div className="w-1.5 h-12 bg-gradient-to-b from-blue-500 to-transparent rounded-full opacity-20"></div>
                                        <div className="bg-white p-2.5 rounded-xl shadow-sm border border-blue-50 flex items-center gap-2 text-xs font-bold text-gray-700">
                                            <Zap className="w-3.5 h-3.5 text-yellow-500" />
                                            Trigger: Form Submission
                                        </div>
                                    </div>
                                    
                                    {/* Action */}
                                    <div className="flex items-center gap-3 pl-4 animate-float" style={{ animationDelay: '1s' }}>
                                        <div className="bg-white p-2.5 rounded-xl shadow-sm border border-blue-50 flex items-center gap-2 text-xs font-bold text-gray-700">
                                            <Smartphone className="w-3.5 h-3.5 text-blue-500" />
                                            Action: Dial Lead
                                        </div>
                                    </div>

                                    {/* Result */}
                                    <div className="flex justify-end pt-2 animate-float" style={{ animationDelay: '2s' }}>
                                        <div className="bg-blue-600 text-white px-4 py-2 rounded-xl shadow-lg shadow-blue-200 flex items-center gap-2 text-sm font-bold transform scale-105">
                                            <CheckCircle className="w-4 h-4" />
                                            Conversion: Appointment Booked
                                        </div>
                                    </div>
                                </div>
                            </div>
                         </div>
                    </div>

                    {/* Text Content */}
                    <div>
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Zap className="w-4 h-4" />
                             INSTANT RESPONSE
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Follow-up in Seconds</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Anya monitors your ad platforms and website forms 24/7. The moment a potential patient hits submit, she initiates a call. This immediate response increases contact rates by up to 400%.
                        </p>
                    </div>
                </div>

                {/* Card 2: Team Integration */}
                <div className="flex flex-col">
                    {/* Visual Area - Glassmorphic on Gradient */}
                    <div className="bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         {/* Background Orbs */}
                         <div className="absolute top-10 left-10 w-40 h-40 bg-indigo-300/30 rounded-full blur-3xl"></div>
                         <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-300/30 rounded-full blur-3xl"></div>
                         
                        <div className="relative w-full max-w-md z-10">
                            {/* Stacked Cards Effect */}
                            <div className="relative h-64 w-full flex flex-col items-center justify-center">
                                
                                {/* Card 3 (Back) */}
                                <div className="absolute top-0 w-[85%] glass-card bg-white/40 p-4 rounded-2xl border border-white/40 shadow-sm transform scale-90 translate-y-4 opacity-60 z-0">
                                    <div className="flex items-center gap-3 opacity-50">
                                        <div className="w-8 h-8 rounded-full bg-indigo-100 p-0.5">
                                            <img src={anaImg} className="w-full h-full rounded-full object-cover" alt="Ana" />
                                        </div>
                                        <div className="h-2 bg-indigo-200 rounded w-24"></div>
                                    </div>
                                </div>

                                {/* Card 2 (Middle) */}
                                <div className="absolute top-8 w-[92%] glass-card bg-white/60 p-4 rounded-2xl border border-white/50 shadow-md transform scale-95 translate-y-2 z-10">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-100 p-0.5">
                                                <img src={amyImg} className="w-full h-full rounded-full object-cover" alt="Amy" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-gray-800">Amy (Receptionist)</p>
                                                <p className="text-xs text-gray-500">Checking schedule...</p>
                                            </div>
                                        </div>
                                        <div className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full">Ready</div>
                                    </div>
                                </div>

                                {/* Card 1 (Front - Main) */}
                                <div className="absolute top-20 w-full glass-card bg-white/80 backdrop-blur-xl p-5 rounded-3xl border border-white/70 shadow-xl z-20 transform hover:-translate-y-1 transition-transform duration-300">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-full bg-blue-100 p-0.5 ring-2 ring-blue-50">
                                                <img src={anyaImg} className="w-full h-full rounded-full object-cover" alt="Anya" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-gray-900">Anya (Marketing)</p>
                                                <p className="text-xs text-blue-600 font-medium">Handoff to Reception</p>
                                            </div>
                                        </div>
                                        <div className="bg-blue-100 p-2 rounded-full text-blue-600">
                                            <Users className="w-4 h-4" />
                                        </div>
                                    </div>
                                    <div className="bg-gray-50/80 rounded-xl p-3 text-xs text-gray-600 leading-relaxed border border-gray-100">
                                        "I've qualified this patient for Invisalign. Handing off to Amy to finalize the appointment slot."
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Text Content */}
                    <div>
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Users className="w-4 h-4" />
                             SEAMLESS HANDOFFS
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Works with the Whole Team</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Anya doesn't work in a silo. She works seamlessly with Amy (Receptionist) and our other agents to ensure the patient experience is smooth from the first click to their first visit.
                        </p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* REFINED SECTION: Intelligent Lead Management (Bento Grid) */}
      <section className="py-24 px-6 bg-white border-t border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-16 max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              A Complete <br />
              <span className="text-blue-600">Lead Conversion System</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              More than just an auto-dialer. Anya is a sophisticated sales agent designed to maximize your marketing ROI.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1 - Intelligent Scheduling (Span 2) */}
            <div className="md:col-span-2 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10 max-w-lg">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-blue-600">
                  <Calendar className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Intelligent Booking</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Anya doesn't just take messages—she books. Following your specific scheduling rules, she places new patients into the correct column and time block directly in Asha PMS.
                </p>
              </div>
              
              {/* Graphic Overlay */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[45%] h-full hidden md:flex items-center justify-center p-6">
                 <div className="relative w-full max-w-xs">
                     <div className="glass-card bg-white/80 p-4 rounded-xl shadow-lg mb-3 transform translate-x-4 border border-white/50">
                         <div className="flex items-center gap-3 mb-2">
                             <div className="bg-blue-100 p-1.5 rounded-lg"><Target className="w-3 h-3 text-blue-600" /></div>
                             <span className="text-xs font-bold text-gray-700">Lead Intent</span>
                         </div>
                         <div className="text-xs text-gray-500">Invisalign Consult -> Dr. Smith (Col 1)</div>
                     </div>

                     <div className="glass-card bg-white/90 p-4 rounded-xl shadow-xl z-10 relative border border-white/60">
                         <div className="flex items-center gap-3 mb-2">
                             <div className="bg-green-100 p-1.5 rounded-lg"><CheckCircle className="w-3 h-3 text-green-600" /></div>
                             <span className="text-xs font-bold text-gray-700">Booking Complete</span>
                         </div>
                         <div className="flex flex-wrap gap-1.5">
                             <span className="text-[9px] bg-green-50 px-2 py-0.5 rounded text-green-600 border border-green-100 font-medium">Tue, 2:00 PM</span>
                             <span className="text-[9px] bg-gray-100 px-2 py-0.5 rounded text-gray-600 border border-gray-200">Confirmed</span>
                         </div>
                     </div>
                 </div>
              </div>
              <div className="absolute right-0 bottom-0 w-1/2 h-full bg-gradient-to-l from-blue-50/50 to-transparent -z-10"></div>
            </div>

            {/* Card 2 - Platform Integration (Span 1) */}
            <div className="md:col-span-1 bg-white rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600">
                  <Layers className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Source Sync</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Works with Facebook Ads, Google Ads, TikTok, and your website forms.
                </p>
                <div className="flex gap-3 opacity-60">
                    {/* Placeholder icons for social platforms */}
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-xs">fb</div>
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold text-xs">G</div>
                    <div className="w-8 h-8 bg-black/10 rounded-full flex items-center justify-center text-black font-bold text-xs">tt</div>
                </div>
              </div>
            </div>

            {/* Card 3 - Sales Training (Span 1) */}
            <div className="md:col-span-1 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-blue-600">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Trained to Sell</h3>
                <p className="text-gray-600 leading-relaxed">
                  Anya is trained on best-in-class dental sales scripts to handle objections, discuss financing, and build value.
                </p>
              </div>
            </div>

            {/* Card 4 - ROI Tracking (Span 1) */}
             <div className="md:col-span-1 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-green-600">
                  <DollarSign className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Revenue Attribution</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  See exactly how much revenue Anya has generated from your ad spend.
                </p>
              </div>
            </div>

            {/* Card 5 - Dashboard (Span 1) */}
             <div className="md:col-span-1 bg-slate-900 rounded-[2rem] p-8 overflow-hidden relative group transition-all hover:shadow-xl text-white">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mb-6 text-blue-400">
                  <MonitorPlay className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">Live Monitoring</h3>
                <p className="text-slate-400 leading-relaxed">
                  Listen to call recordings and read transcripts to see how your leads are being handled.
                </p>
              </div>
               <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:16px_16px]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700 shadow-xl border border-blue-500/50">
            <div className="grid lg:grid-cols-2 gap-0 items-center">
              {/* Left: Content */}
              <div className="p-10 lg:p-12 relative z-10">
                <p className="text-xs font-bold text-blue-100 tracking-wider mb-3 uppercase">
                  MAXIMIZE YOUR AD SPEND
                </p>
                
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white leading-tight">
                  Stop letting ad leads slip through the cracks.
                </h2>
                
                <p className="text-base text-blue-100 mb-6 leading-relaxed max-w-xl">
                  Practices using Anya see a significant increase in conversion rates and production per month. Turn your ad clicks into booked patients automatically.
                </p>
                
                <Link to="/book-demo">
                  <button className="group px-7 py-3 rounded-xl font-bold text-sm transition-all bg-white text-blue-600 hover:bg-blue-50 shadow-lg hover:shadow-xl">
                    <span className="flex items-center gap-2">
                      Start Converting Leads
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </Link>
              </div>

              {/* Right: Graphic Composition */}
              <div className="relative h-full min-h-[400px] flex items-center justify-center p-8 overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
                </div>

                {/* Main Card */}
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-72 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Live Call</span>
                    </div>
                    <span className="text-xs font-mono text-gray-400">01:12</span>
                  </div>

                  {/* Avatar Area */}
                  <div className="flex flex-col items-center mb-6">
                    <div className="relative mb-3">
                      <div className="w-20 h-20 rounded-full p-1 bg-gradient-to-br from-blue-100 to-indigo-100">
                        <img
                          src={anyaImg}
                          alt="Anya"
                          className="w-full h-full rounded-full object-cover"
                        />
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm">
                        <div className="bg-blue-600 rounded-full p-1">
                          <Phone className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Anya</h3>
                    <p className="text-sm text-blue-600 font-medium">Marketing Coordinator</p>
                  </div>

                  {/* Waveform Visualization */}
                  <div className="flex items-center justify-center gap-1 h-8 mb-4">
                    {[1, 2, 3, 2, 4, 3, 5, 4, 2, 3, 1, 2].map((h, i) => (
                      <div
                        key={i}
                        className="w-1 bg-blue-500 rounded-full animate-pulse"
                        style={{ height: `${h * 20}%`, animationDelay: `${i * 0.1}s` }}
                      ></div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg py-2 text-center">
                      <span className="text-xs font-semibold text-gray-600">Qualifying</span>
                    </div>
                    <div className="bg-gray-50 rounded-lg py-2 text-center">
                      <span className="text-xs font-semibold text-gray-600">Booking</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarketingCoordinatorPage;






























